import { useEffect } from 'react';

interface VehicleSchemaProps {
  publication: any;
  selectedVersion?: string;
  baseUrl?: string;
}

/**
 * Componente para inyectar datos estructurados Vehicle Schema en la página
 * Genera JSON-LD compatible con Google Rich Results
 */
export const VehicleSchema = ({ 
  publication, 
  selectedVersion,
  baseUrl = 'https://linzecar.cl'
}: VehicleSchemaProps) => {
  useEffect(() => {
    if (!publication) return;

    // Obtener datos del vehículo seleccionado si aplica
    let vehicleData = publication;
    if (selectedVersion && publication.vehicles) {
      const selected = publication.vehicles.find((v: any) => v._id === selectedVersion);
      if (selected) {
        vehicleData = { ...publication, ...selected };
      }
    }

    // Construir el esquema Vehicle
    const schema = {
      '@context': 'https://schema.org/',
      '@type': 'Vehicle',
      name: publication.title || `${publication.year} ${publication.brand} ${publication.model}`,
      description: publication.chatgpt_description_summary || publication.user_description || '',
      
      // Información del vehículo
      vehicleIdentificationNumber: publication.vin || undefined,
      vehicleModelDate: publication.year ? `${publication.year}` : undefined,
      vehicleConfiguration: publication.version || undefined,
      bodyType: publication.body_type || undefined,
      fuelType: publication.fuel || undefined,
      transmission: publication.transmission || undefined,
      mileageFromOdometer: {
        '@type': 'QuantitativeValue',
        value: publication.mileage || 0,
        unitCode: 'KMT'
      },
      
      // Precio
      offers: {
        '@type': 'Offer',
        price: publication.price || 0,
        priceCurrency: 'CLP',
        availability: publication.sold ? 'OutOfStock' : 'InStock',
        url: `${baseUrl}/autos/${publication.condition === 'used' ? 'usados' : 'nuevos'}/${(publication.brand || '').toLowerCase().replace(/\s+/g, '-')}/${(publication.model || '').toLowerCase().replace(/\s+/g, '-')}/${publication.year}/${publication.id}`
      },
      
      // Imágenes
      image: publication.images && publication.images.length > 0 
        ? publication.images.map((img: any) => img.url || img).filter(Boolean)
        : [],
      
      // Información del vendedor
      seller: {
        '@type': publication.dealership ? 'Organization' : 'Person',
        name: publication.dealership?.name || publication.dealership?.business_name || 'Vendedor',
        ...(publication.dealership?.address && { address: publication.dealership.address })
      },
      
      // Información adicional
      color: publication.color || undefined,
      numberOfDoors: publication.doors || undefined,
      seatingCapacity: publication.seats || undefined,
      
      // Condición del vehículo
      vehicleCondition: publication.condition === 'used' ? 'UsedCondition' : 'NewCondition',
      
      // Ubicación
      ...(publication.region && {
        areaServed: {
          '@type': 'Place',
          name: publication.region
        }
      })
    };

    // Filtrar valores undefined
    const cleanSchema = JSON.parse(JSON.stringify(schema));

    // Crear o actualizar el script de datos estructurados
    let scriptTag = document.querySelector('script[data-vehicle-schema]') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      (scriptTag as HTMLScriptElement).type = 'application/ld+json';
      scriptTag.setAttribute('data-vehicle-schema', 'true');
      document.head.appendChild(scriptTag);
    }
    
    (scriptTag as HTMLScriptElement).textContent = JSON.stringify(cleanSchema);

    return () => {
      // Limpiar al desmontar
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, [publication, selectedVersion, baseUrl]);

  return null; // Este componente solo inyecta datos, no renderiza nada
};

/**
 * Componente para inyectar Product Schema (alternativa a Vehicle Schema)
 * Útil para vehículos nuevos o cuando se quiere enfatizar el aspecto de producto
 */
export const ProductSchema = ({ 
  publication, 
  baseUrl = 'https://linzecar.cl'
}: Omit<VehicleSchemaProps, 'selectedVersion'>) => {
  useEffect(() => {
    if (!publication) return;

    const schema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: publication.title || `${publication.year} ${publication.brand} ${publication.model}`,
      description: publication.chatgpt_description_summary || publication.user_description || '',
      image: publication.images && publication.images.length > 0 
        ? publication.images.map((img: any) => img.url || img).filter(Boolean)
        : [],
      brand: {
        '@type': 'Brand',
        name: publication.brand || ''
      },
      offers: {
        '@type': 'Offer',
        price: publication.price || 0,
        priceCurrency: 'CLP',
        availability: publication.sold ? 'OutOfStock' : 'InStock',
        url: `${baseUrl}/autos/${publication.condition === 'used' ? 'usados' : 'nuevos'}/${(publication.brand || '').toLowerCase().replace(/\s+/g, '-')}/${(publication.model || '').toLowerCase().replace(/\s+/g, '-')}/${publication.year}/${publication.id}`
      },
      aggregateRating: publication.rating ? {
        '@type': 'AggregateRating',
        ratingValue: publication.rating,
        reviewCount: publication.reviewCount || 1
      } : undefined
    };

    const cleanSchema = JSON.parse(JSON.stringify(schema));

    let scriptTag = document.querySelector('script[data-product-schema]') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      (scriptTag as HTMLScriptElement).type = 'application/ld+json';
      scriptTag.setAttribute('data-product-schema', 'true');
      document.head.appendChild(scriptTag);
    }
    
    (scriptTag as HTMLScriptElement).textContent = JSON.stringify(cleanSchema);

    return () => {
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, [publication, baseUrl]);

  return null;
};

/**
 * Componente para inyectar Brand Schema
 * Útil en páginas de marca
 */
export const BrandSchema = ({ 
  brandName,
  brandLogo,
  description,
  baseUrl = 'https://linzecar.cl'
}: {
  brandName: string;
  brandLogo?: string;
  description?: string;
  baseUrl?: string;
}) => {
  useEffect(() => {
    if (!brandName) return;

    const schema = {
      '@context': 'https://schema.org/',
      '@type': 'Brand',
      name: brandName,
      ...(brandLogo && { logo: brandLogo }),
      ...(description && { description }),
      url: `${baseUrl}/marcas-autos-0km`
    };

    const cleanSchema = JSON.parse(JSON.stringify(schema));

    let scriptTag = document.querySelector('script[data-brand-schema]') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      (scriptTag as HTMLScriptElement).type = 'application/ld+json';
      scriptTag.setAttribute('data-brand-schema', 'true');
      document.head.appendChild(scriptTag);
    }
    
    (scriptTag as HTMLScriptElement).textContent = JSON.stringify(cleanSchema);

    return () => {
      if (scriptTag) {
        scriptTag.remove();
      }
    };
  }, [brandName, brandLogo, description, baseUrl]);

  return null;
};

export default VehicleSchema;
